import React from 'react';
import { useBLoC } from 'hooks/useBLoC';
import { useInitBloc } from 'hooks/useInitBloc';
import { BLoCBase, BLoCParams, renderBlocChild } from 'types/BLoCBase';
import { CodeframesModalTypes } from './CodeframesModal.types';
import { useForm, UseFormReturn } from 'react-hook-form';
import { $get, $patch, $post } from 'services/api';
import { catchError, debounceTime, finalize, of, shareReplay, switchMap, tap } from 'rxjs';
import { snackbarV2Error, snackbarV2Success } from 'services/snackbarV2';
import { Codeframe } from 'types/Codeframe';
import { getCustomerStore } from 'stores/customer.store';

type State = {
  loading: 'creatingCodeframe' | 'editingCodeframe' | 'loaded';
  customers?: { name: string; id: string }[];
  currentCodeframe?: Codeframe | null;
};
class FormValues {
  public name: string = '';
  public customer: { name: string; id: string } | null = null;
  public files: File[] = [];
  public type: string = 'thematic';
}

class BLoC extends BLoCBase<State> {
  public $customers = this.$getState('customers');
  public $loading = this.$getState('loading');

  constructor(
    public readonly form: UseFormReturn<FormValues>,
    public readonly onClose: CodeframesModalTypes.CodeframesModalProps['onClose'],
    public readonly codeframe?: Codeframe,
    public readonly isAdmin?: boolean
  ) {
    super({ loading: 'loaded' });
    if (codeframe) {
      this.form.reset({
        name: codeframe.name,
        customer: codeframe.customer,
        type: codeframe.type,
      });
      this.setState('customers', [codeframe.customer]);
    }
  }

  public isEditCodeframe = !!this.codeframe;

  public getCustomers = (search?: string) =>
    this.addSub(
      $get<{ name: string; id: string }[]>(
        'customers',
        {
          ...(search && { search }),
        },
        { root: '/admin-api/' }
      )
        .pipe(
          debounceTime(300),
          catchError(() => []),
          shareReplay()
        )
        .subscribe((data) => this.setState('customers', data)),
      'getCustomers'
    );

  private $currentCodeframe = (data: FormValues) => {
    return this.currentState('currentCodeframe')
      ? of(this.currentState('currentCodeframe')!)
      : (this.isAdmin
          ? $post<Codeframe>(
              'tc/codeframes',
              {
                name: data.name,
                customer_id: data.customer?.id,
                type: data.type,
              },
              { root: '/admin-api/' }
            )
          : getCustomerStore().$customerSlug.pipe(
              switchMap((customerSlug) =>
                $post<Codeframe>(`tc/customers/${customerSlug}/codeframes`, {
                  name: data.name,
                  type: data.type,
                })
              )
            )
        ).pipe(tap((codeframe) => this.setState('currentCodeframe', codeframe)));
  };

  private $uploadFile = (codeframId: string, file: File) => {
    const fd = new FormData();
    fd.append('csv_file', file, file.name);
    fd.append('_method', 'PATCH');
    return $post(`tc/codeframes/${codeframId}/upload-csv`, fd, {
      requestType: 'form',
      ...(this.isAdmin && { root: '/admin-api/' }),
    });
  };

  public createCodeFrame = this.form.handleSubmit((data) => {
    const file = data.files[0];
    this.setState('loading', 'creatingCodeframe');
    this.addSub(
      this.$currentCodeframe(data)
        .pipe(
          switchMap((codeFrame) => this.$uploadFile(codeFrame.id, file)),
          finalize(() => this.setState('loading', 'loaded'))
        )
        .subscribe({
          next: () => {
            snackbarV2Success({
              title: 'Codeframe created.',
            });
            this.onClose(true);
          },
          error: (err) =>
            snackbarV2Error({
              title: err?.message || 'An error ocurred. Try again later.',
            }),
        })
    );
  });

  public editCodeFrame = this.form.handleSubmit((data) => {
    const file = data.files?.length ? data.files[0] : null;
    this.setState('loading', 'editingCodeframe');
    this.addSub(
      $patch<Codeframe>(
        `tc/codeframes/${this.codeframe?.id}`,
        {
          name: data.name,
        },
        {
          ...(this.isAdmin && { root: '/admin-api/' }),
        }
      )
        .pipe(
          switchMap((codeFrame) => (file ? this.$uploadFile(codeFrame.id, file) : of(null))),
          finalize(() => this.setState('loading', 'loaded'))
        )
        .subscribe({
          next: () => {
            snackbarV2Success({
              title: 'Codeframe edited.',
            });
            this.onClose(true);
          },
          error: (err) =>
            snackbarV2Error({
              title: err?.message || 'An error ocurred. Try again later.',
            }),
        }),
      'editCodeFrame'
    );
  });

  public closeModal = () => this.onClose(!!this.currentState('currentCodeframe'));
}

const Context = React.createContext<Readonly<BLoC>>({} as any);

export const useCodeframesModalBLoC = () => useBLoC<BLoC>(Context);

export const CodeframesModalBLoC: React.FC<
  BLoCParams<BLoC, State> & CodeframesModalTypes.CodeframesModalProps
> = ({ children, codeframe, onClose, isAdmin }) => {
  const form = useForm({
    defaultValues: new FormValues(),
    mode: 'all',
  });
  const bloc = useInitBloc(() => new BLoC(form, onClose, codeframe, isAdmin));
  return bloc ? <Context.Provider value={bloc}>{renderBlocChild(children, bloc)}</Context.Provider> : null;
};
